import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { Table, Loader, Select } from '@mantine/core';
import { getCurrencyName, formatCurrAmount } from '../../utils/script';
import { ICurrency, ICurrencyValuesForm } from '../../../libraries/currencies';
import { useState, useEffect, useRef } from 'react';
import { IUser } from '../../../libraries/user';
import { useParams } from 'react-router-dom';
import CurrencyInput from '../../components/currency-input';
import { CurrencyEditHook } from '../../utils/hooks';
import * as Icon from '@tabler/icons-react';

interface CurrencyTableProps {
  selectedCurrencies: {
    gid: number;
    eid?: number;
    entid?: number;
    cid: number;
    minAmount: number;
    maxAmount: number;
    decAmount?: number;
  }[];
  currencyList: ICurrency[];
  loading: boolean;
  editable: boolean;
  filteredCid?: ICurrency[];
  onAddCurrency?: (currency: ICurrencyValuesForm) => void;
  onRemoveCurrency?: (cid: number) => void;
  entity: { entid: number | 0; gid?: number | null };
  eid?: string;
  form: any;
  declarationAmount?: number;
  isNewLocation?: boolean;
  editingCell?: { rowIndex: number; column: string } | null;
  cellValue?: number | string;
  currencyEdit?: CurrencyEditHook;
  onCurrencyChange?: (index: number, field: 'minAmount' | 'maxAmount' | 'decAmount', centsValue: number) => void;
  maxCurrencies: number;
}

const CurrencyTable = ({
  selectedCurrencies,
  currencyList,
  loading,
  editable,
  filteredCid,
  form,
  declarationAmount,
  isNewLocation,
  onAddCurrency,
  onRemoveCurrency,
  onCurrencyChange,
  maxCurrencies
}: CurrencyTableProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const loginInfo = useSelector<AppState, IUser>(state => state.login.user);
  const { eid } = useParams<string>();
  const tableRef = useRef<HTMLDivElement>(null);

  const [updatedCurrencies, setUpdatedCurrencies] = useState<
    { gid: number; eid?: number; cid: number; minAmount: number; maxAmount: number; decAmount?: number }[]
  >(selectedCurrencies || []);

  const [editingCell, setEditingCell] = useState<{ rowIndex: number; column: string } | null>(null);

  useEffect(() => {
    setUpdatedCurrencies(
      (selectedCurrencies || []).map(currency => ({
        gid: loginInfo.gid ?? 0,
        eid: Number(eid) || 0,
        cid: currency.cid,
        minAmount: currency.minAmount ?? 0,
        maxAmount: currency.maxAmount ?? 0,
        decAmount: currency.decAmount ?? 0
      }))
    );
  }, [selectedCurrencies, eid, loginInfo.gid]);

  const updateCurrencyValue = (
    rowIndex: number,
    field: 'minAmount' | 'maxAmount' | 'decAmount',
    centsValue: number
  ) => {
    setUpdatedCurrencies(prev => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [field]: centsValue };

      form.setFieldValue('currencies', updated);
      onCurrencyChange?.(rowIndex, field, centsValue);

      return updated;
    });
  };

  const handleCurrencySelect = (currencyCid: string | null) => {
    if (!currencyCid) return;
    if (updatedCurrencies.length >= maxCurrencies) return;

    const selectedCurrency = filteredCid?.find(c => c.cid.toString() === currencyCid);

    if (!selectedCurrency) return;

    const newCurr: ICurrencyValuesForm = {
      gid: loginInfo.gid ?? 0,
      eid: eid ? Number(eid) : undefined,
      entid: loginInfo.entid,
      cid: selectedCurrency.cid,
      minAmount: 0,
      maxAmount: 0,
      decAmount: 0
    };

    setUpdatedCurrencies(prev => {
      if (prev.some(curr => curr.cid === newCurr.cid)) return prev;

      const updated = [...prev, newCurr];

      form.setFieldValue('currencies', updated);
      onAddCurrency?.(newCurr);

      return updated;
    });
  };

  const availableCurrencies =
    filteredCid?.filter(currency => !updatedCurrencies.some(selected => selected.cid === currency.cid)) || [];

  return (
    <>
      {editable && availableCurrencies.length > 0 && (
        <Select
          label={lang.GLOBAL_CURRENCY}
          placeholder={lang.EQUIPMENT_CURRENCY_SELECT}
          data={
            availableCurrencies.map(currency => ({
              value: currency.cid.toString(),
              label: getCurrencyName(currency.cid)
            })) || []
          }
          onChange={handleCurrencySelect}
          value={''}
          disabled={(!editable && !isNewLocation) || updatedCurrencies.length >= maxCurrencies}
        />
      )}

      <Table.ScrollContainer minWidth={100} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.GLOBAL_NAME}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_STATE_LABEL_MIN_AMOUNT}</Table.Th>
              {(declarationAmount || isNewLocation) && <Table.Th>{lang.EQUIPMENT_STATE_LABEL_DEC_AMOUNT}</Table.Th>}
              <Table.Th>{lang.EQUIPMENT_STATE_LABEL_MAX_AMOUNT}</Table.Th>
              {(!editable && !isNewLocation) || <Table.Th></Table.Th>}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : updatedCurrencies.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              updatedCurrencies.map((currency, rowIndex) => {
                const matchingCurrency = currencyList?.find(c => c.cid === currency.cid);

                return (
                  <Table.Tr key={currency.cid}>
                    <Table.Td>{getCurrencyName(currency.cid)}</Table.Td>

                    {/* Min Amount */}
                    <Table.Td
                      onClick={() => editable && setEditingCell({ rowIndex, column: 'minAmount' })}
                      style={{ cursor: editable ? 'pointer' : 'default' }}
                    >
                      {editingCell?.rowIndex === rowIndex && editingCell.column === 'minAmount' ? (
                        <CurrencyInput
                          value={currency.minAmount}
                          onChange={value => updateCurrencyValue(rowIndex, 'minAmount', value)}
                          onFinish={() => setEditingCell(null)}
                        />
                      ) : matchingCurrency ? (
                        formatCurrAmount(currency.minAmount, matchingCurrency)
                      ) : (
                        '-'
                      )}
                    </Table.Td>

                    {(declarationAmount || isNewLocation) && (
                      <Table.Td
                        onClick={() => editable && setEditingCell({ rowIndex, column: 'decAmount' })}
                        style={{ cursor: editable ? 'pointer' : 'default' }}
                      >
                        {editingCell?.rowIndex === rowIndex && editingCell.column === 'decAmount' ? (
                          <CurrencyInput
                            value={currency.decAmount || 0}
                            onChange={value => updateCurrencyValue(rowIndex, 'decAmount', value)}
                            onFinish={() => setEditingCell(null)}
                          />
                        ) : matchingCurrency ? (
                          formatCurrAmount(currency.decAmount ?? 0, matchingCurrency)
                        ) : (
                          '-'
                        )}
                      </Table.Td>
                    )}

                    <Table.Td
                      onClick={() => editable && setEditingCell({ rowIndex, column: 'maxAmount' })}
                      style={{ cursor: editable ? 'pointer' : 'default' }}
                    >
                      {editingCell?.rowIndex === rowIndex && editingCell.column === 'maxAmount' ? (
                        <CurrencyInput
                          value={currency.maxAmount || 0}
                          onChange={value => updateCurrencyValue(rowIndex, 'maxAmount', value)}
                          onFinish={() => setEditingCell(null)}
                        />
                      ) : matchingCurrency ? (
                        formatCurrAmount(currency.maxAmount, matchingCurrency)
                      ) : (
                        '-'
                      )}
                    </Table.Td>

                    {editable && (
                      <Table.Td>
                        <Icon.IconX stroke={2} onClick={() => onRemoveCurrency?.(currency.cid)} />
                      </Table.Td>
                    )}
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </>
  );
};

export default CurrencyTable;
